import type { FC, KeyboardEvent, PointerEvent, ReactNode } from 'react'
import type { OrbitState, SolarView, ViewPreset } from '@/components/landing/solar-array/orbit'

import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { Move3d } from 'lucide-react'
import { useMotionValueEvent, useScroll } from 'motion/react'
import { cn } from '@/lib/utils'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'

import {
    DEFAULT_VIEW,
    PITCH_MAX,
    PITCH_MIN,
    VIEWS,
    clamp,
    closestYaw
} from '@/components/landing/solar-array/orbit'

interface DragState {
    id: number
    x: number
    y: number
    axis: 'free' | 'x' | null
    time: number
    velocity: number
}

// The 3D scene only exists in the browser, so it loads on its own after the page.
const SolarArrayScene = dynamic(() => import('@/components/landing/solar-array/solar-array-scene'), {
    ssr: false,
    loading: () => null
})

const YAW_PER_PIXEL = 0.0075
const PITCH_PER_PIXEL = 0.005
const VIEW_HOLD_MS = 6000

const PhotoFallback: FC = (): ReactNode => {
    return (
        <Image
            src='/images/solar-array.jpg'
            alt=''
            fill
            sizes='(min-width: 1024px) 1024px, 100vw'
            className='object-contain'
        />
    )
}

// An interactive, real-time 3D solar array: drag to turn it, use the arrow keys,
// or jump to a side with the view buttons. It turns slowly on its own when left alone.
const SolarArrayViewer: FC = (): ReactNode => {
    const stageRef = useRef<HTMLDivElement>(null)
    const dragRef = useRef<DragState | null>(null)
    const touchedRef = useRef(false)
    const reduced = usePrefersReducedMotion()
    const [active, setActive] = useState(false)
    const [ready, setReady] = useState(false)
    const [grabbing, setGrabbing] = useState(false)
    const [touched, setTouched] = useState(false)
    const [view, setView] = useState<SolarView>(DEFAULT_VIEW.id)

    const orbit = useRef<OrbitState>({
        targetYaw: DEFAULT_VIEW.yaw,
        targetPitch: DEFAULT_VIEW.pitch,
        yaw: DEFAULT_VIEW.yaw,
        pitch: DEFAULT_VIEW.pitch,
        intro: 0,
        lastInteraction: 0,
        dragging: false,
        reduced: false
    })

    // The array swings round into its resting angle as the section scrolls into view.
    const { scrollYProgress } = useScroll({ target: stageRef, offset: ['start end', 'center center'] })

    useMotionValueEvent(scrollYProgress, 'change', (value) => {
        orbit.current.intro = value
    })

    useEffect(() => {
        orbit.current.intro = scrollYProgress.get()
    }, [scrollYProgress])

    useEffect(() => {
        orbit.current.reduced = reduced
    }, [reduced])

    // Only render frames while the model is on screen.
    useEffect(() => {
        const stage = stageRef.current

        if (!stage) return

        const observer = new IntersectionObserver(([entry]) => setActive(entry.isIntersecting), { rootMargin: '200px 0px' })

        observer.observe(stage)

        return () => observer.disconnect()
    }, [])

    const markTouched = (): void => {
        orbit.current.lastInteraction = performance.now()

        if (touchedRef.current) return

        touchedRef.current = true
        setTouched(true)
    }

    const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
        if (event.pointerType === 'mouse' && event.button !== 0) return

        const isMouse = event.pointerType === 'mouse'

        dragRef.current = {
            id: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            axis: isMouse ? 'free' : null,
            time: performance.now(),
            velocity: 0
        }

        if (isMouse) {
            event.currentTarget.setPointerCapture(event.pointerId)
            orbit.current.dragging = true
            setGrabbing(true)
        }

        markTouched()
    }

    const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
        const drag = dragRef.current

        if (!drag || drag.id !== event.pointerId) return

        const dx = event.clientX - drag.x
        const dy = event.clientY - drag.y

        // On touch screens a vertical swipe keeps scrolling the page; only a sideways swipe turns the model.
        if (drag.axis === null) {
            if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return

            if (Math.abs(dy) > Math.abs(dx)) {
                dragRef.current = null

                return
            }

            drag.axis = 'x'
            event.currentTarget.setPointerCapture(event.pointerId)
            orbit.current.dragging = true
        }

        const now = performance.now()
        const state = orbit.current
        const turn = -dx * YAW_PER_PIXEL

        state.targetYaw += turn

        if (drag.axis === 'free') {
            state.targetPitch = clamp(state.targetPitch + dy * PITCH_PER_PIXEL, PITCH_MIN, PITCH_MAX)
        }

        drag.velocity = (turn / Math.max(now - drag.time, 1)) * 16
        drag.x = event.clientX
        drag.y = event.clientY
        drag.time = now
        markTouched()
    }

    const endDrag = (event: PointerEvent<HTMLDivElement>): void => {
        const drag = dragRef.current

        if (!drag || drag.id !== event.pointerId) return

        const state = orbit.current

        // A quick flick carries the turn on a little after release.
        if (state.dragging && performance.now() - drag.time < 80) {
            state.targetYaw += clamp(drag.velocity * 12, -1.6, 1.6)
        }

        state.dragging = false
        state.lastInteraction = performance.now()
        dragRef.current = null
        setGrabbing(false)
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        const steps: Record<string, [number, number]> = {
            ArrowLeft: [0.3, 0],
            ArrowRight: [-0.3, 0],
            ArrowUp: [0, 0.12],
            ArrowDown: [0, -0.12]
        }

        const step = steps[event.key]

        if (!step) return

        event.preventDefault()

        const state = orbit.current

        state.targetYaw += step[0]
        state.targetPitch = clamp(state.targetPitch + step[1], PITCH_MIN, PITCH_MAX)
        markTouched()
    }

    const goTo = (preset: ViewPreset): void => {
        const state = orbit.current

        state.targetYaw = closestYaw(state.yaw, preset.yaw)
        state.targetPitch = preset.pitch
        markTouched()
        state.lastInteraction = performance.now() + VIEW_HOLD_MS
    }

    const caption = VIEWS.find((preset) => preset.id === view)?.caption ?? DEFAULT_VIEW.caption

    return (
        <div>
            <div
                ref={stageRef}
                role='group'
                tabIndex={0}
                aria-roledescription='3D model'
                aria-label='Solar panel array. Drag it, or use the arrow keys, to turn it and see every side.'
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onKeyDown={onKeyDown}
                className={cn(
                    'relative aspect-[4/3] w-full touch-pan-y select-none rounded-[2rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 sm:aspect-[16/9]',
                    grabbing ? 'cursor-grabbing' : 'cursor-grab'
                )}
            >
                <div aria-hidden='true' className={cn('absolute inset-0 transition-opacity duration-700', ready ? 'opacity-0' : 'opacity-100')}>
                    <PhotoFallback />
                </div>

                <div className={cn('absolute inset-0 transition-opacity duration-700', ready ? 'opacity-100' : 'opacity-0')}>
                    <SolarArrayScene
                        orbit={orbit}
                        active={active}
                        onViewChange={setView}
                        onReady={() => setReady(true)}
                        fallback={<PhotoFallback />}
                    />
                </div>

                <p
                    aria-hidden='true'
                    className={cn(
                        'pointer-events-none absolute left-1/2 top-3 inline-flex -translate-x-1/2 items-center gap-2 rounded-full bg-white/85 px-3.5 py-1.5 text-xs font-medium text-primary-strong shadow-[0_8px_24px_-12px_rgba(14,26,18,0.35)] ring-1 ring-black/5 backdrop-blur transition-opacity duration-500 sm:top-5',
                        touched || !ready ? 'opacity-0' : 'opacity-100'
                    )}
                >
                    <Move3d className='size-4' />
                    Drag to turn it
                </p>
            </div>

            <div className='mt-6 flex flex-col items-center gap-4'>
                <div role='group' aria-label='Choose a side to look at' className='inline-flex rounded-full bg-mist p-1 ring-1 ring-black/5'>
                    {VIEWS.map((preset) => (
                        <button
                            key={preset.id}
                            type='button'
                            aria-pressed={view === preset.id}
                            onClick={() => goTo(preset)}
                            className={cn(
                                'h-9 rounded-full px-4 text-sm font-medium transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-5',
                                view === preset.id ? 'bg-primary-strong text-white shadow-sm' : 'text-primary-strong/70 hover:text-primary-strong'
                            )}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>

                <p aria-live='polite' className='min-h-[2.75rem] max-w-md text-balance text-center text-sm leading-relaxed text-primary-strong/75 sm:min-h-0'>
                    {caption}
                </p>
            </div>
        </div>
    )
}

export default SolarArrayViewer
